import { useState, useCallback } from "react";
import { isBefore, fromUnixTime } from 'date-fns';
import { useDispatch } from "react-redux";
import { getCasperPublicKey } from "../utils/casper-wallet";

import { Args, CLTypeUInt8, CLValue, Hash, PublicKey, SessionBuilder, TransactionV1, HttpHandler, RpcClient, URef, CLTypeList, CLTypePublicKey, CLValueList, ParamDictionaryIdentifier, ParamDictionaryIdentifierContractNamedKey, ContractCallBuilder, CLTypeUInt32, CLValueParser, CLTypeKey, CLTypeUInt512, CLTypeUInt64, CLTypeBool, PurseIdentifier } from "casper-js-sdk";
import { getContractMainPurse, getStateItem, getStakeInfo } from "../utils/casper-utils";
import { BigNumber } from "@ethersproject/bignumber";
import { VALIDATORSSTATE_IDX } from "../utils/config";
import { setCasperPublicKey } from "../redux/casperSlice";

const UNSTAKE_IDS_STATE_IDX = 4;
const UNSTAKE_INFO_STATE_IDX = 80;

const useCasperWallet = (click) => {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  const fetchPublicKey = useCallback(async () => {
    setError(null);
    try {
      const key = await getCasperPublicKey();
      dispatch(setCasperPublicKey(key));
      return key;
    } catch (err) {
      setError(err);
      return null;
    }
  }, [dispatch]);

  const getValidatorsDictionaryItemKey = async (rpcClient, contractHash) => {
    return getStateItem(rpcClient, contractHash, VALIDATORSSTATE_IDX, null);
  };

  const getSCSPRTotalSupply = async (rpcClient, contractHash) => {
    const totalSupply = await rpcClient.queryLatestGlobalState("hash-" + contractHash, ["total_supply"]);
    return totalSupply.storedValue.clValue.ui256.getValue();
  };

  async function getBalance(publicKeyHex, nodeUrl) {
    try {
      const rpcHandler = new HttpHandler(window.csprclick.appSettings.casper_node, 'fetch');
      rpcHandler.setCustomHeaders({ Authorization: window.csprclick.digestAuth });
      const rpcClient = new RpcClient(rpcHandler);
      const purseIdentifier = PurseIdentifier.fromPublicKey(PublicKey.fromHex(publicKeyHex));
      const result = await rpcClient.queryLatestBalance(purseIdentifier);
      return result?.balance?.value?.toString(); // balance in motes
    } catch (error) {
      console.error('Failed to get balance:', error);
      throw error;
    }
  }

  // Move getCsprScprRate inside the hook and wrap with useCallback
  const getCsprScprRate = useCallback(async (nodeUrl, contractHash) => {
    const rpcHandler = new HttpHandler(nodeUrl);
    const rpcClient = new RpcClient(rpcHandler);

    const contractMainPurse = await getContractMainPurse(rpcClient, contractHash);
    const bytes = await getValidatorsDictionaryItemKey(rpcClient, contractHash);
    const list = CLValueList.fromBytes(bytes, new CLTypeList(CLTypePublicKey)).result;

    let totalStaked = BigNumber.from(0);
    for (const el of list.elements) {
      const validatorPk = el.publicKey;
      const [, stakedAmount] = await getStakeInfo(rpcClient, validatorPk, URef.fromString(contractMainPurse));
      totalStaked = totalStaked.add(stakedAmount);
    }

    const totalSupply = await getSCSPRTotalSupply(rpcClient, contractHash);
    const totalStakedDecimal = parseFloat(totalStaked.toString());
    const totalSupplyDecimal = parseFloat(totalSupply.toString());
    const ratio = totalStakedDecimal / totalSupplyDecimal;

    return {
      csprStaked: totalStaked.toString(),
      scsprSupply: totalSupply.toString(),
      rate: ratio
    };
  }, []);

  const getScsprBalance = useCallback(async (nodeUrl, contractHash, publicKey) => {
    const rpcHandler = new HttpHandler(nodeUrl);
    const rpcClient = new RpcClient(rpcHandler);

    const accHash = PublicKey.fromHex(publicKey).accountHash();
    //convert accountHash to bytes without using Buffer
    //prepend 00 to accountHash to make it 32 bytes long
    const accountHashBytes = new Uint8Array(33);
    accountHashBytes[0] = 0;
    accountHashBytes.set(accHash.toBytes(), 1);

    const binary = String.fromCharCode(...accountHashBytes);
    const accountHashBase64 = btoa(binary);

    console.log('accountHashBase64', accountHashBase64);
    const paramDictionaryIdentifier = new ParamDictionaryIdentifier(
        undefined,
        new ParamDictionaryIdentifierContractNamedKey(
            `hash-${contractHash}`,
            'balances',
            accountHashBase64
        ),
        undefined,
        undefined
    );
    debugger;
    const result = await rpcClient.getDictionaryItemByIdentifier(null, paramDictionaryIdentifier);
    const scsprBalance = result.storedValue.clValue?.ui256?.toNumber();

    return scsprBalance;
  }, []); 

  // Stake using Casper Wallet provider
  const stake = useCallback(
    async ({
      networkName,
      contractPackageHash,
      entryPoint = 'stake',
      amount,
      paymentAmount,
      publicKey,
      onStatusUpdate,
    }) => {
      try {
        const clickRef = window.csprclick;

        onStatusUpdate?.('preparing', { message: 'Preparing transaction...' });

        const proxyBytes = await fetch('/proxy_caller.wasm');
        const contractWasm = new Uint8Array(await proxyBytes.arrayBuffer());

        const args_bytes = new Uint8Array([0x00, 0x00, 0x00, 0x00]);
        const serialized_args = CLValue.newCLList(CLTypeUInt8,
            Array.from(args_bytes)
                .map(value => CLValue.newCLUint8(value))
        );

        const args = Args.fromMap({
            amount: CLValue.newCLUInt512(amount),
            attached_value: CLValue.newCLUInt512(amount),
            entry_point: CLValue.newCLString(entryPoint),
            package_hash: CLValue.newCLByteArray(Hash.fromHex(contractPackageHash).toBytes()),
            args: serialized_args,
        });

        const sessionTransaction = new SessionBuilder()
            .from(PublicKey.fromHex(publicKey))
            .runtimeArgs(args)
            .wasm(new Uint8Array(contractWasm))
            .payment(paymentAmount) // Amount in motes
            .chainName(networkName)
            .build();

        const tx = { transaction: { Version1: TransactionV1.toJSON(sessionTransaction.getTransactionV1()) } };

        onStatusUpdate?.('signing', { message: 'Waiting for wallet signature...' });

        const onStatusUpdateCallback = (status, data) => {
          console.log('STATUS UPDATE', status, data);
          onStatusUpdate?.(status, data);
        };

        const res = await clickRef.send(tx, publicKey, onStatusUpdateCallback);
        
        if (res?.transactionHash) {
          onStatusUpdate?.('completed', { 
            transactionHash: res.transactionHash,
            message: 'Transaction completed successfully'
          });
          return res.transactionHash;
        } else {
          onStatusUpdate?.('failed', { 
            message: 'Transaction failed - no hash returned'
          });
          return null;
        }
      } catch (err) {
        console.error('Error during staking:', err);
        onStatusUpdate?.('failed', { 
          error: err,
          message: 'Transaction failed: ' + (err?.message || err)
        });
        setError(err);
        return null;
      }
    },
    []
  );

  const unstake = useCallback(
    async ({
      networkName,
      contractPackageHash,
      entryPoint = 'unstake',
      amount,
      paymentAmount,
      publicKey,
      onStatusUpdate,
    }) => {
      try {
        const clickRef = window.csprclick;

        onStatusUpdate?.('preparing', { message: 'Preparing unstake transaction...' });

        const args = Args.fromMap({
            scspr_amount: CLValue.newCLUInt256(amount),
        });

        const transaction = new ContractCallBuilder()
            .from(PublicKey.fromHex(publicKey))
            .byPackageHash(contractPackageHash)
            .entryPoint(entryPoint)
            .runtimeArgs(args)
            .payment(paymentAmount) // Amount in motes
            .chainName(networkName)
            .build()
            .getTransactionV1();
      
        const tx = { transaction: { Version1: TransactionV1.toJSON(transaction) } };

        onStatusUpdate?.('signing', { message: 'Waiting for wallet signature...' });

        const onStatusUpdateCallback = (status, data) => {
          console.log('UNSTAKE STATUS UPDATE', status, data);
          onStatusUpdate?.(status, data);
        };

        const res = await clickRef.send(tx, publicKey, onStatusUpdateCallback);
        
        if (res?.transactionHash) {
          onStatusUpdate?.('completed', { 
            transactionHash: res.transactionHash,
            message: 'Unstake transaction completed successfully'
          });
          return res.transactionHash;
        } else {
          onStatusUpdate?.('failed', { 
            message: 'Unstake transaction failed - no hash returned'
          });
          return null;
        }
      } catch (err) {
        console.error('Error during unstaking:', err);
        onStatusUpdate?.('failed', { 
          error: err,
          message: 'Unstake transaction failed: ' + (err?.message || err)
        });
        return null;
      }
    }, 
    []
  )

  const getAddressClaims = useCallback(async (nodeUrl, contractHash, publicKey) => {
    const rpcHandler = new HttpHandler(nodeUrl);
    const rpcClient = new RpcClient(rpcHandler);

    const accountHash = PublicKey.fromHex(publicKey).accountHash().toJSON().replace("account-hash", "");

    const unstakeIds = await getUnstakeIdsDictionaryItemKey(rpcClient, contractHash, accountHash);
    let claimable = 0;
    let pending = 0;
    const pendingList = [];
    const nowDate = new Date();
    for (const unstakeId of unstakeIds) {
      const unstakeInfoResult = await getUnstakeInfoDictionaryItemKey(rpcClient, contractHash, unstakeId);
      const amount = unstakeInfoResult.amount.ui512.toNumber();
      let claimTime = unstakeInfoResult.claimTime.ui64.toNumber();
      const isClaimed = unstakeInfoResult.isClaimed.bool.getValue();
      // Handle both seconds and ms timestamps
      if (claimTime > 1e12) {
        // Milliseconds
        claimTime = Math.floor(claimTime / 1000);
      }
      const claimDate = fromUnixTime(claimTime);
      if (!isClaimed && (isBefore(claimDate, nowDate) || claimDate.getTime() === nowDate.getTime())) {
        claimable += amount;
      } else if (!isClaimed && isBefore(nowDate, claimDate)) {
        pending += amount;
        pendingList.push({
          amount,
          claimTime,
          unstakeId,
        });
      }
    }
    return {
      claimable,
      pending,
      pendingList
    };
  }, []);

  async function getUnstakeIdsDictionaryItemKey(
    rpcClient,
    contract_hash,
    accountHashHex
  ) {
    if (accountHashHex.length !== 64) {
        throw new Error('Account hash must be 32 bytes (64 hex characters)');
    }

    const bytes = [];
    bytes.push(0x00);

    const accountHashBytes = Uint8Array.from(Buffer.from(accountHashHex, 'hex'));
    bytes.push(...Array.from(accountHashBytes));

    const unstakeIdsBytes =  await getStateItem(rpcClient, contract_hash, UNSTAKE_IDS_STATE_IDX, new Uint8Array(bytes));
    const list = CLValueList.fromBytes(unstakeIdsBytes, new CLTypeList(CLTypeUInt32)).result;

    const unstakeIds = [];
    for (const el of list.elements) {
        const unstakeId = el.ui32.toNumber();
        unstakeIds.push(unstakeId);
    }

    return unstakeIds;
  }

  async function getUnstakeInfoDictionaryItemKey(
    rpcClient,
    contract_hash,
    unstakeId
  ){
    // Step 2: Construct the input byte array
    const bytes = [];

    // Push the unstake Id
    bytes.push(unstakeId & 0xff);
    bytes.push((unstakeId >> 8) & 0xff);
    bytes.push((unstakeId >> 16) & 0xff);
    bytes.push((unstakeId >> 24) & 0xff);

    const stateBytes =  await getStateItem(rpcClient, contract_hash, UNSTAKE_INFO_STATE_IDX, new Uint8Array(bytes));

    const getCLValue = (bytes, type) => {
        const clValueWithRemainder = CLValueParser.fromBytesByType(
            bytes,
            type,
        );
        if (!clValueWithRemainder.bytes) {
            throw new Error('remainder is empty');
        }
        const remainder = clValueWithRemainder.bytes;
        return {
            value: clValueWithRemainder.result,
            remainder,
        };
    }

    const { value: id, remainder: stateBytes2 } = getCLValue(stateBytes, CLTypeUInt32);
    const { value: owner, remainder: stateBytes3 } = getCLValue(stateBytes2, CLTypeKey);
    const { value: amount, remainder: stateBytes4 } = getCLValue(stateBytes3, CLTypeUInt512);
    const { value: claimTime, remainder: stateBytes5 } = getCLValue(stateBytes4, CLTypeUInt64);
    const { value: isClaimed } = getCLValue(stateBytes5, CLTypeBool);

    return {
        id,
        owner,
        amount,
        claimTime,
        isClaimed,
    }
  }

  const withdrawCspr = useCallback(
    async ({
      networkName,
      contractPackageHash,
      entryPoint = 'claim',
      paymentAmount,
      publicKey,
      onStatusUpdate,
    }) => {
      try {
        const clickRef = window.csprclick;

        onStatusUpdate?.('preparing', { message: 'Preparing withdraw transaction...' });

        const args = Args.fromMap({});

        const transaction = new ContractCallBuilder()
            .from(PublicKey.fromHex(publicKey))
            .byPackageHash(contractPackageHash)
            .entryPoint(entryPoint)
            .runtimeArgs(args)
            .payment(paymentAmount) // Amount in motes
            .chainName(networkName)
            .build()
            .getTransactionV1();
      
        const tx = { transaction: { Version1: TransactionV1.toJSON(transaction) } };

        onStatusUpdate?.('signing', { message: 'Waiting for wallet signature...' });

        const onStatusUpdateCallback = (status, data) => {
          console.log('WITHDRAW STATUS UPDATE', status, data);
          onStatusUpdate?.(status, data);
        };

        const res = await clickRef.send(tx, publicKey, onStatusUpdateCallback);
        
        if (res?.transactionHash) {
          onStatusUpdate?.('completed', { 
            transactionHash: res.transactionHash,
            message: 'Withdraw transaction completed successfully'
          });
          return res.transactionHash;
        } else {
          onStatusUpdate?.('failed', { 
            message: 'Withdraw transaction failed - no hash returned'
          });
          return null;
        }
      } catch (err) {
        console.error('Error during withdraw:', err);
        onStatusUpdate?.('failed', { 
          error: err,
          message: 'Withdraw transaction failed: ' + (err?.message || err)
        });
        return null;
      }
    }, []
  )


  return {
    error,
    getPublicKey: fetchPublicKey,
    getScsprBalance,
    getCsprScprRate,
    stake,
    click,
    unstake,
    getAddressClaims,
    withdrawCspr,
    getBalance
  };
};

export default useCasperWallet;
